import React from 'react';
import { Query } from '@apollo/client/react/components';
import { mapAfter } from 'apollo-relay/update-query';
import Helmet from 'helmet/helmet';
import PropTypes from 'prop-types';
import Styled from 'styled';

import LoadingProgress from 'lib/react-router/loading-progress';
import observe from 'lib/intersection-observer/observe';
import btoa from 'lib/base64/btoa';
import atob from 'lib/base64/atob';

import gaTrack from 'four-nets/lib/google-analytics/track';
import ErrorPage from 'four-nets/errors/error-page';
import { FIRST, PAGE_SIZE, AUTO_LOAD_COUNT } from 'four-nets/photoblog/feed/feed-loader';
import Feed from 'four-nets/photoblog/feed/feed';
import FetchMoreButton, { adjacentHrefsFromCursor } from 'four-nets/ui-v2/fetch-more-button/fetch-more-button';
import OffsetPager from 'four-nets/ui-v2/pager/offset-pager';
import { VendorFragment } from 'four-nets/vendor/vendor/vendor.graphql';
import { hideExpiredVendorContent } from 'four-nets/vendor/permissions';

import query, { UserFeedLoaderQuery, UserFeedLoaderQueryVariables, PostType } from './user-feed-loader.graphql';
import defaultStyles from './user-blog.scss';

export interface Props {
    styles: typeof defaultStyles;
    vendor: Partial<VendorFragment>;
    username: string;
    type?: string;
    albumId?: string;
    imageId?: string;
    slug?: string;
    postType?: PostType;
    offset?: number;
    pathname: string;
}

interface State {
    loading?: boolean;
    visible: boolean;
    autoLoadCount: number;
    infinite: boolean;
}

class UserFeedLoader extends React.PureComponent<Props, State> {
    static contextTypes = {
        scrollView: PropTypes.object,
    };

    fetchMore?: () => Promise<any> = undefined;
    refetch?: () => Promise<any>;
    unobserve?: () => void;
    ref: React.RefObject<HTMLDivElement>;

    constructor(props: Props) {
        super(props);
        this.ref = React.createRef();
        this.state = {
            visible: false,
            autoLoadCount: AUTO_LOAD_COUNT - 1,
            infinite: false,
            loading: false,
        };
    }

    componentDidMount() {
        const element = this.ref && (this.ref.current as Element | null);
        if (element) {
            this.unobserve = observe(
                element,
                () => {
                    this.setState({ visible: true });
                },
                { rootMargin: '300px' }
            );
        }
    }

    componentWillUnmount() {
        if (this.unobserve) {
            this.unobserve();
        }
    }

    initObserveElement = (force: boolean = false) => {
        const element = this.ref && (this.ref.current as Element | null);
        if (element && (!this.unobserve || force)) {
            if (this.unobserve) {
                this.unobserve();
                this.unobserve = undefined;
            }
            this.unobserve = observe(
                element,
                () => {
                    this.setState({ visible: true });
                },
                { rootMargin: '350px' }
            );
            this.setState(prevState => ({
                autoLoadCount: AUTO_LOAD_COUNT,
                visible: false,
                infinite: prevState.infinite || force,
            }));
        }
    };

    componentDidUpdate(prevProps: Props, prevState: State) {
        this.initObserveElement();

        const { infinite, autoLoadCount, loading, visible } = this.state;

        /* If we already loaded the page for X times, we unobserve the fetchMore button  */
        let autoLoadActive = true;
        if (!infinite && prevState.autoLoadCount > 0 && autoLoadCount <= 0) {
            if (this.unobserve) {
                this.unobserve();
                this.unobserve = undefined;
            }
            autoLoadActive = false;
        }

        /* When the fetchMore button appeared, we want to fetchMore if autoload is still active */
        const appeared = visible && !prevState.visible;
        if (autoLoadActive && appeared && !loading && this.fetchMore) {
            this.fetchMore().then(() => {
                this.setState(prevState => ({ visible: false, autoLoadCount: prevState.autoLoadCount - 1 }));
            });
        }

        const offsetChange = prevProps.offset !== this.props.offset;
        if (offsetChange) {
            this.setState({ autoLoadCount: AUTO_LOAD_COUNT - 1 });
            this.context.scrollView.toTop();
        }
    }

    renderHelmet = (H: any) => {
        return (
            <H>
                <meta name='robots' content='noindex' />
            </H>
        );
    };

    render() {
        const { styles, postType, username, slug, imageId, offset, vendor } = this.props;
        const { autoLoadCount } = this.state;

        let after: string | undefined;
        if (offset) {
            after = btoa(`arrayconnection:${offset - 1}`) || undefined;
        }

        const variables: UserFeedLoaderQueryVariables = {
            first: slug ? 1 : FIRST,
            username,
            postType,
            slug,
            after,
        };

        return hideExpiredVendorContent(vendor as VendorFragment) ? null : (
            <Query<UserFeedLoaderQuery, UserFeedLoaderQueryVariables>
                query={query}
                variables={variables}
                onCompleted={() => {
                    if (!after) {
                        this.setState({ visible: false });
                    }
                }}
                fetchPolicy='cache-first'
            >
                {({ error, data, loading, refetch, fetchMore }) => {
                    const endCursor = data && data.feed && data.feed.pageInfo.endCursor;

                    this.fetchMore =
                        data && data.feed && data.feed.pageInfo && data.feed.pageInfo.hasNextPage
                            ? () => {
                                  this.setState({ loading: true });

                                  return fetchMore({
                                      variables: { after: endCursor },
                                      updateQuery: (
                                          previousResult = {} as UserFeedLoaderQuery,
                                          { fetchMoreResult = {} as UserFeedLoaderQuery }
                                      ) => {
                                          return {
                                              ...previousResult,
                                              ...fetchMoreResult,
                                              feed: mapAfter(previousResult.feed, fetchMoreResult.feed),
                                          };
                                      },
                                  }).finally(() => this.setState({ loading: false }));
                              }
                            : undefined;

                    let nextOffset;
                    const [endCursorHref, prevCursorHref] = adjacentHrefsFromCursor({
                        endCursor,
                        pathname: this.props.pathname,
                        pageSize: FIRST,
                    });

                    if (endCursor) {
                        nextOffset = parseInt(atob(endCursor).split(':')[1], 10) + 1;
                    } else {
                        nextOffset = offset;
                    }

                    return (
                        <LoadingProgress loading={loading} block={false}>
                            <ErrorPage error={error}>
                                {data && data.feed && data.feed.edges && data.feed.edges.length === 0 ? (
                                    <Helmet render={this.renderHelmet} />
                                ) : null}
                                <Feed
                                    data={data}
                                    loading={loading}
                                    refetch={refetch}
                                    imageId={imageId}
                                    nonMainFeed={true}
                                    openComments={Boolean(slug)}
                                />
                                <div ref={this.ref} className={styles.fetchMoreWrapper}>
                                    {this.fetchMore ? (
                                        <FetchMoreButton
                                            disabled={this.state.loading}
                                            loading={this.state.loading}
                                            href={endCursorHref}
                                            prevHref={prevCursorHref}
                                            onClick={() => {
                                                if (this.fetchMore) {
                                                    this.fetchMore().then(() => {
                                                        this.setState(prevState => ({
                                                            visible: false,
                                                            autoLoadCount: prevState.autoLoadCount - 1,
                                                        }));
                                                    });

                                                    if (typeof window !== 'undefined') {
                                                        gaTrack('pageview', endCursorHref);
                                                    }
                                                }
                                            }}
                                        />
                                    ) : null}
                                    {(data &&
                                        data.feed &&
                                        data.feed.edges!.length !== 0 &&
                                        data.feed.edges!.length % PAGE_SIZE === 0) ||
                                    autoLoadCount <= 0 ? (
                                        <OffsetPager pageSize={PAGE_SIZE} offset={nextOffset} />
                                    ) : null}
                                </div>
                            </ErrorPage>
                        </LoadingProgress>
                    );
                }}
            </Query>
        );
    }
}

export default Styled(defaultStyles)(UserFeedLoader);
